import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./Motion";
import { SmartImage } from "./SmartImage";
import { MENU, AVG_COST } from "@/data/cafe";
import { useDialogs } from "./DialogProvider";
import { QrCode, Plus } from "lucide-react";

export default function MenuSection() {
  const [active, setActive] = useState(MENU[0].key);
  const { open, addToCart } = useDialogs();
  const cat = MENU.find((c) => c.key === active);

  return (
    <section id="menu" className="bg-[#1A1514] py-24 md:py-32" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Overline>Our Menu</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F2EB] mt-4 font-medium">
              Crafted for every <span className="italic text-[#1C7C54]">craving</span>
            </h2>
            <p className="font-body text-sm text-[#F5F2EB]/55 mt-4" data-testid="avg-cost-note">
              Average cost {AVG_COST}
            </p>
          </div>
          <button onClick={() => open("order")} data-testid="menu-qr-btn"
            className="self-start inline-flex items-center gap-2 rounded-sm border border-[#F5F2EB]/25 text-[#F5F2EB] px-5 py-3 font-accent text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-colors">
            <QrCode className="h-4 w-4" /> QR Menu / Order
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mt-10 border-b border-white/10 pb-5">
          {MENU.map((c) => (
            <button key={c.key} onClick={() => setActive(c.key)} data-testid={`menu-tab-${c.key}`}
              className={`rounded-sm px-4 py-2 font-accent text-xs uppercase tracking-[0.15em] transition-colors ${
                active === c.key ? "bg-[#1C7C54] text-[#F5F2EB]" : "text-[#F5F2EB]/60 hover:text-[#F5F2EB] border border-white/15"
              }`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Bento layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden rounded-sm h-64 md:h-full min-h-[360px] relative">
              <AnimatePresence mode="wait">
                <motion.div key={cat.key} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="h-full">
                  <SmartImage src={cat.image} alt={cat.label} className="h-full w-full object-cover" />
                </motion.div>
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1514]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="font-display text-4xl text-[#F5F2EB]">{cat.label}</p>
              </div>
            </div>
          </Reveal>

          <div className="md:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div key={cat.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
                className="max-h-[560px] overflow-y-auto chat-scroll pr-1">
                {cat.subcategories.map((sub) => (
                  <div key={sub.label} className="mb-8 last:mb-0">
                    <p className="overline text-[#1C7C54] mb-1">{sub.label}</p>
                    <div className="divide-y divide-white/10 border-t border-white/10">
                      {sub.items.map((item) => (
                        <div key={item.name} className="flex items-start gap-4 py-4 group" data-testid={`menu-item-${item.name}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-3">
                              <h3 className="font-display text-xl md:text-2xl text-[#F5F2EB] group-hover:text-[#1C7C54] transition-colors">{item.name}</h3>
                              <span className="flex-1 border-b border-dotted border-white/20 translate-y-[-4px]" />
                              <span className="font-accent text-[#1C7C54] text-base md:text-lg whitespace-nowrap">₹{item.price}</span>
                            </div>
                            <p className="font-body text-sm text-[#F5F2EB]/50 mt-1">{item.desc}</p>
                          </div>
                          <button onClick={() => addToCart({ name: item.name, price: item.price })} data-testid={`add-to-cart-${item.name}`}
                            aria-label={`Add ${item.name} to cart`}
                            className="mt-1 shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#1C7C54]/50 text-[#1C7C54] px-3.5 py-1.5 font-accent text-[10px] uppercase tracking-[0.12em] hover:bg-[#1C7C54] hover:text-[#F5F2EB] transition-colors">
                            <Plus className="h-3.5 w-3.5" /> Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
